'use client';

import { useTranslation } from '@/app/i18n/client';
import { useAuthV2 } from '@/hooks/useAuthV2';
import useCart from '@/hooks/useCart';
import { formatCurrency } from '@/utils/currency';
import { Experience, Order, Session, TicketType, UserGroups } from '@/utils/types';
import { InfoIcon } from 'lucide-react';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Quantity } from '../quantity';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface TicketTypesProps {
  experience: Experience;
  session?: Session;
  onAddToCart: (ticket: TicketType, amount: number) => void;
  onRemoveFromCart: (ticket: TicketType, amount: number) => void;
  className?: string;
  disabled?: boolean;
  disablePlus?: boolean;
}

export const TicketTypes: React.FC<TicketTypesProps> = ({
  className,
  disabled,
  disablePlus,
  experience,
  session,
  onAddToCart,
  onRemoveFromCart,
}) => {
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, 'tickets');
  const { getCart } = useCart();
  const [showInfo, setShowInfo] = useState<{ [key: string]: boolean }>({});
  const { userGroups } = useAuthV2();
  const isAdmin = userGroups?.includes(UserGroups.STAFF) || userGroups?.includes(UserGroups.ADMIN);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>(session?.assignedTickets || []);

  const getTicketCountInCart = (ticketId: string) => {
    const cart = getCart();

    const total = cart.reduce((total: number, order: Order) => {
      if (
        order.experience.id === experience.id &&
        order.startDate === session?.startsAt &&
        order.endDate === session?.endsAt
      ) {
        return (
          total +
          order.tickets.reduce(
            (orderTotal, ticket) => (ticket.id === ticketId ? orderTotal + ticket.amount : orderTotal),
            0
          )
        );
      }
      return total;
    }, 0);

    return total;
  };

  const getRemainingTickets = (ticketId: string) => {
    const cart = getCart();

    const totalInCart = cart.reduce((total: number, order: Order) => {
      if (
        order.experience.id === experience.id &&
        order.startDate === session?.startsAt &&
        order.endDate === session?.endsAt
      ) {
        return total + order.tickets.reduce((orderTotal, ticket) => orderTotal + ticket.amount, 0);
      }
      return total;
    }, 0);

    const totalAvailable = session?.totalTicketsAvailable || 0;
    return Math.max(0, totalAvailable - totalInCart);
  };

  useEffect(() => {
    setTicketTypes(session?.assignedTickets || []);
  }, [session]);

  const toggleModal = (ticketId: string) => {
    setShowInfo((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  if (!session || !ticketTypes.length) return <p className='text-base text-zinc-600'>{t('selectSession')}</p>;

  return (
    <div className={`flex flex-col justify-center ${className}`}>
      {ticketTypes.map((ticket) => {
        if (ticket.privateTicketOffice && !isAdmin) return null;

        return (
          <div
            key={ticket.id}
            className='flex select-none items-center justify-between border-t border-gray2 py-6'
            style={{
              opacity: disabled ? 0.5 : 1,
            }}>
            <div>
              <div className='flex items-center justify-start'>
                <p className='w-fit text-lg font-light max-sm:text-base'>{ticket.name}</p>
                {Boolean(ticket.description) && (
                  <>
                    <div className='hidden md:block'>
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon className='relative -top-[1.7px] ml-2 h-5 w-5' />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className='max-w-96 text-base'>{ticket.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className='md:hidden'>
                      <InfoIcon onClick={() => toggleModal(ticket.id)} className='relative -top-[1.6px] ml-2 h-5 w-5' />
                      <Dialog open={showInfo[ticket.id] || false} onOpenChange={() => toggleModal(ticket.id)}>
                        <DialogContent className='w-[calc(100%-2rem)] max-w-lg'>
                          <DialogTitle className='text-lg font-light'>{ticket.name}</DialogTitle>
                          <DialogDescription className='text-base'>{ticket.description}</DialogDescription>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </>
                )}
              </div>

              {ticket.price?.cents > 0 ? (
                <p className='text-zinc-600'>
                  {formatCurrency(ticket.price?.cents || 0, lng as string)}
                  {ticket.fee?.cents > 0 && (
                    <span className='inline font-normal'>
                      &nbsp;
                      {t('feePerTicket', { fee: formatCurrency(ticket.fee?.cents || 0, lng as string) })}
                    </span>
                  )}
                </p>
              ) : (
                <p className='font-semibold text-success-600'>{t('freeTicket')}</p>
              )}
            </div>

            <Quantity
              className='' // max-sm:hidden
              disabled={disabled}
              disablePlus={disablePlus}
              incrementValue={experience.minCanBePurchasedInExperience}
              max={experience.maxCanBePurchasedInDay}
              key={`quantity-${session.startsAt}-${ticket.id}`}
              defaultValue={getTicketCountInCart(ticket.id)}
              onIncrement={(amount: number) => onAddToCart(ticket, amount)}
              onDecrement={(amount: number) => onRemoveFromCart(ticket, amount)}
              remainingTickets={getRemainingTickets(ticket.id)}
              minCanBePurchasedInExperience={experience.minCanBePurchasedInExperience}
            />

            {/* <SelectQuantity
              className='hidden max-sm:flex'
              disabled={disabled}
              disablePlus={disablePlus}
              incrementValue={experience.minCanBePurchasedInExperience}
              max={experience.maxCanBePurchasedInDay}
              key={`select-quantity-${session.startsAt}-${ticket.id}`}
              defaultValue={getTicketCountInCart(ticket.id)}
              onIncrement={(amount: number) => onAddToCart(ticket, amount)}
              onDecrement={(amount: number) => onRemoveFromCart(ticket, amount)}
            /> */}
          </div>
        );
      })}
    </div>
  );
};
